/**
 * Entropy Heat Map Component
 * Visual heat map for selecting focus area for entropy reduction.
 * Shows entropy scores for all domains with color-coded tiles.
 * @feature BRIDGE_SESSION_FLOW - Entropy heat map step in session flow
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, Modal, Button, Space, Typography, Tooltip, Collapse } from 'antd';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { EntropyScore, EntropyMapResponse } from '../../types/bridgeSession';
import { getEntropyInterpretation } from '../../types/entropy';
import { REM_FACTORS, EEM_FACTORS, ENTROPY_FACTOR_LABELS } from '../../config/entropyHierarchy';
import { EntropyFrictionBars, FactorData } from './EntropyFrictionBars';
import { ActualVsIdealChart } from './ActualVsIdealChart';
import styles from './EntropyHeatMap.module.css';

const { Panel } = Collapse;

const { Title, Text } = Typography;

export const EntropyHeatMap: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const workshopSessionId = searchParams.get('sessionId') || sessionId;
  const { sessionType, selectEntropyArea, navigateToStep, sessionState, setActiveObjective, scope, scopeValidation } = useBridgeSession();
  const [entropyMap, setEntropyMap] = useState<EntropyMapResponse | null>(null);
  const [scores, setScores] = useState<EntropyScore[]>([]);
  const [filteredScores, setFilteredScores] = useState<EntropyScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState<EntropyScore | null>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [scopeValidationError, setScopeValidationError] = useState<string | null>(null);

  // Load entropy map with artifact_item details
  useEffect(() => {
    if (!sessionId) return;

    const loadEntropyMap = async () => {
      try {
        setLoading(true);
        // Try new entropy map endpoint first
        try {
          const scopeArtifactIds = sessionState?.entropyHeatMap.scope_artifact_ids;
          const response = await bridgeSessionService.getEntropyMap(sessionId, scopeArtifactIds, sessionType);
          setEntropyMap(response);
          setScores(response.scores);
        } catch (error) {
          // Fallback to legacy endpoint
          console.warn('Entropy map endpoint not available, using legacy endpoint:', error);
          const response = await bridgeSessionService.getEntropyScores(sessionId, sessionType);
          setScores(response.scores);
        }
      } catch (error) {
        console.error('Failed to load entropy map:', error);
        setScores([]);
        setEntropyMap(null);
        // No mock fallback: entropy data must come from the bridge session
      } finally {
        setLoading(false);
      }
    };

    loadEntropyMap();
  }, [sessionId, sessionType, sessionState]);

  // Filter scores by scope
  useEffect(() => {
    if (!scope && !scopeValidation) {
      // No scope defined, show all scores
      setFilteredScores(scores);
      return;
    }

    // Filter scores based on scope validation
    if (scopeValidation) {
      const includedArtifactIds = new Set(
        scopeValidation.included_artifacts.map((a: { artifact_id: string }) => a.artifact_id)
      );
      const filtered = scores.filter(score => 
        includedArtifactIds.has(score.artifact_id) || 
        includedArtifactIds.has(score.artifact_item_id)
      );
      setFilteredScores(filtered);
    } else if (scope) {
      // If we have scope but no validation, use scope directly
      // Check if score's artifact_id or artifact_item_id is in descendant list
      if (scope.descendant_artifact_ids && scope.descendant_artifact_ids.length > 0) {
        const descendantIds = new Set(scope.descendant_artifact_ids);
        const filtered = scores.filter(score =>
          descendantIds.has(score.artifact_id) ||
          descendantIds.has(score.artifact_item_id)
        );
        setFilteredScores(filtered);
      } else {
        // No specific descendants, show all (scope will be applied by backend)
        setFilteredScores(scores);
      }
    } else {
      setFilteredScores(scores);
    }
  }, [scores, scope, scopeValidation]);

  // Build scopeMap (factorId -> { entropy, correction, label }) for friction bars
  const scopeMap = useMemo(() => {
    const map = new Map<string, FactorData>();
    if (entropyMap?.hierarchy) {
      for (const dg of entropyMap.hierarchy) {
        for (const sub of dg.subdomains) {
          const factorId = sub.artifact_item_id.replace(/^item-/, '').split('-')[0] || sub.artifact_item_id.replace(/^item-/, '');
          map.set(factorId, {
            entropy: sub.entropy_score,
            correction: sub.correction_factor ?? 0,
            label: sub.subdomain_name,
          });
        }
      }
    }
    if (map.size === 0 && scores.length > 0) {
      for (const s of scores) {
        const factorId = s.artifact_item_id.replace(/^item-/, '').split('-')[0] || s.artifact_name;
        if (factorId && (REM_FACTORS.includes(factorId) || EEM_FACTORS.includes(factorId))) {
          map.set(factorId, {
            entropy: s.entropy_score,
            correction: s.correction_factor ?? 0,
            label: ENTROPY_FACTOR_LABELS[factorId] || s.artifact_name,
          });
        }
      }
    }
    return map;
  }, [entropyMap, scores]);

  const getEntropyColor = (score: number): string => {
    if (score >= 7) return '#cf1322'; // Red (worst)
    if (score >= 5) return '#fa8c16'; // Orange (5-6)
    if (score >= 4) return '#fadb14'; // Yellow (4)
    if (score >= 3) return '#95de64'; // Light green (3)
    return '#389e0d'; // Deep green (1-2, best)
  };

  const handleTileClick = (score: EntropyScore) => {
    setSelectedArea(score);
    setConfirmModalVisible(true);
  };

  const handleSegmentClick = (factorId: string, artifactItemId: string) => {
    if (entropyMap?.hierarchy) {
      for (const dg of entropyMap.hierarchy) {
        const sub = dg.subdomains.find((s) => s.artifact_item_id === artifactItemId || s.artifact_item_id === `item-${factorId}`);
        if (sub) {
          handleTileClick(subdomainToScore(dg.domain_name, sub));
          return;
        }
      }
    }
    const score = scores.find((s) => s.artifact_item_id === artifactItemId || s.artifact_name === factorId);
    if (score) handleTileClick(score);
  };

  const handleConfirm = async () => {
    if (!sessionId || !selectedArea) return;

    setConfirmModalVisible(false);

    if (isLocalBridgeApiEnabled()) {
      selectEntropyArea(selectedArea.artifact_item_id);
      setActiveObjective(selectedArea.artifact_item_id);
      navigateToStep('friction_priorities');
      return;
    }

    try {
      const response = await bridgeSessionService.createObjective(sessionId, {
        target_subdomain_artifact_item_id: selectedArea.artifact_item_id,
        target_correction_factor: selectedArea.correction_factor ? selectedArea.correction_factor * 0.8 : 1.2,
        improvement_objective: `Reduce entropy in ${selectedArea.artifact_name} from ${selectedArea.entropy_score} to target level`,
      });
      selectEntropyArea(selectedArea.artifact_item_id);
      setActiveObjective(response.objective_artifact_item_id);
      navigateToStep('bridge_card');
    } catch (error) {
      console.error('Failed to create objective:', error);
    }
  };

  const handleCancel = () => {
    setConfirmModalVisible(false);
    setSelectedArea(null);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Text>Loading entropy map...</Text>
      </div>
    );
  }

  // Helper to check if a score is in scope
  const isInScope = (score: EntropyScore): boolean => {
    if (!scope && !scopeValidation) return true;
    if (scopeValidation) {
      return scopeValidation.included_artifacts.some(
        (a: { artifact_id: string }) => a.artifact_id === score.artifact_id || a.artifact_id === score.artifact_item_id
      );
    }
    if (scope?.descendant_artifact_ids) {
      return scope.descendant_artifact_ids.includes(score.artifact_id) ||
        scope.descendant_artifact_ids.includes(score.artifact_item_id);
    }
    return true;
  };

  // Build a score-like object from hierarchy subdomain for tiles and selection
  const subdomainToScore = (
    domainName: string,
    sub: { artifact_item_id: string; subdomain_name: string; entropy_score: number; correction_factor?: number }
  ): EntropyScore => ({
    artifact_item_id: sub.artifact_item_id,
    artifact_id: `artifact-${sub.artifact_item_id}`,
    artifact_name: sub.subdomain_name,
    domain_name: domainName,
    entropy_score: sub.entropy_score,
    correction_factor: sub.correction_factor,
    state: 'ACTIVE',
    calculated_at: new Date().toISOString(),
  });

  const hasHierarchy = entropyMap?.hierarchy && entropyMap.hierarchy.length > 0;
  const defaultOpenKeys: string[] = []; // Start with subdomains collapsed

  return (
    <div className={styles.entropyHeatMap}>
      <div className={styles.header}>
        <Title level={2}>Entropy Heat Map</Title>
        <Text type="secondary">
          Select an area where reducing entropy would materially improve the business. Business → Finance, Operations, Strategy, Revenue, Resources → factors.
        </Text>
        <div className={styles.entropyLegend} aria-label="Entropy scale">
          <span><span className={styles.legendSwatch} style={{ backgroundColor: getEntropyColor(1) }} /> 1 = Ideal</span>
          <span><span className={styles.legendSwatch} style={{ backgroundColor: getEntropyColor(7) }} /> 7 = High Chaos</span>
        </div>
      </div>

      {/* Chart 1: Actual vs Ideal revenue and expense (above heat map) */}
      {entropyMap?.metrics && (
        <ActualVsIdealChart metrics={entropyMap.metrics} />
      )}

      {/* Business summary row when we have rollup hierarchy */}
      {hasHierarchy && entropyMap?.business_entropy_score != null && (
        <div
          className={styles.businessSummary}
          style={{ borderLeftColor: getEntropyColor(entropyMap.business_entropy_score) }}
        >
          <span>Business</span>
          <span
            className={styles.panelHeaderBadge}
            style={{ backgroundColor: getEntropyColor(entropyMap.business_entropy_score) }}
          >
            {entropyMap.business_entropy_score}/10
          </span>
        </div>
      )}

      {/* Chart 2: Friction breakdown by factor (Revenue REM, Expense EEM) */}
      {scopeMap.size > 0 && (
        <EntropyFrictionBars
          scopeMap={scopeMap}
          onSegmentClick={handleSegmentClick}
          remCorrection={entropyMap?.metrics?.rem_current}
          eemCorrection={entropyMap?.metrics?.eem_current}
        />
      )}

      {/* Hierarchical display: 5 subdomains (Finance, Operations, Strategy, Revenue, Resources) with rollup color on header */}
      {hasHierarchy ? (
        <Collapse defaultActiveKey={defaultOpenKeys}>
          {entropyMap!.hierarchy.map((domainGroup) => {
            const rollupScore = domainGroup.rollup_entropy_score ?? 0;
            const rollupColor = getEntropyColor(rollupScore);
            return (
              <Panel
                key={domainGroup.domain_id}
                header={
                  <div className={styles.panelHeader}>
                    <div
                      className={styles.panelHeaderBar}
                      style={{ backgroundColor: rollupColor }}
                    />
                    <span className={styles.panelHeaderTitle}>
                      <Text strong>{domainGroup.domain_name}</Text>
                      {domainGroup.subdomains.length > 0 && (
                        <Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>
                          {domainGroup.subdomains.length} factor{domainGroup.subdomains.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                    </span>
                    {rollupScore > 0 && (
                      <span
                        className={styles.panelHeaderBadge}
                        style={{ backgroundColor: rollupColor }}
                      >
                        {rollupScore}/10
                      </span>
                    )}
                  </div>
                }
              >
                <div className={styles.subdomainGrid}>
                  {domainGroup.subdomains.map((sub) => {
                    const score = subdomainToScore(domainGroup.domain_name, sub);
                    const interpretation = getEntropyInterpretation(sub.entropy_score);
                    const inScope = isInScope(score);
                    return (
                      <Tooltip
                        key={sub.artifact_item_id}
                        title={
                          <div>
                            {sub.correction_factor != null && (
                              <div>Correction Factor: {sub.correction_factor.toFixed(2)}</div>
                            )}
                            <div>{interpretation.description}</div>
                          </div>
                        }
                      >
                        <Card
                          className={`${styles.entropyTile} ${selectedArea?.artifact_item_id === score.artifact_item_id ? styles.selected : ''} ${!inScope ? styles.outOfScope : ''}`}
                          style={{
                            borderLeftColor: getEntropyColor(sub.entropy_score),
                            borderLeftWidth: '4px',
                            opacity: inScope ? 1 : 0.5,
                          }}
                          hoverable={inScope}
                          onClick={() => inScope && handleTileClick(score)}
                        >
                          <div className={styles.tileContent}>
                            <div className={styles.tileHeader}>
                              <Text strong>{sub.subdomain_name}</Text>
                              <div
                                className={styles.scoreBadge}
                                style={{ backgroundColor: getEntropyColor(sub.entropy_score) }}
                              >
                                {sub.entropy_score}/10
                              </div>
                            </div>
                            {sub.correction_factor != null && (
                              <div className={styles.tileMetadata}>
                                <Text type="secondary" className={styles.tileCorrectionFactor}>
                                  CF: {sub.correction_factor.toFixed(2)}
                                </Text>
                              </div>
                            )}
                            <Text type="secondary" className={styles.tileDescription}>
                              {interpretation.description}
                            </Text>
                          </div>
                        </Card>
                      </Tooltip>
                    );
                  })}
                </div>
              </Panel>
            );
          })}
        </Collapse>
      ) : (
        /* Fallback: Flat list if hierarchy not available */
        <div className={styles.tilesGrid}>
          {filteredScores.map((score) => {
            const interpretation = getEntropyInterpretation(score.entropy_score);
            const inScope = isInScope(score);
            return (
              <Tooltip
                key={score.artifact_item_id}
                title={
                  <div>
                    <div>State: {score.state}</div>
                    {score.correction_factor && (
                      <div>Correction Factor: {score.correction_factor.toFixed(2)}</div>
                    )}
                    {score.ideal_value && score.actual_value && (
                      <div>
                        Ideal: {score.ideal_value} | Actual: {score.actual_value}
                      </div>
                    )}
                  </div>
                }
              >
                <Card
                  className={`${styles.entropyTile} ${selectedArea?.artifact_item_id === score.artifact_item_id ? styles.selected : ''} ${!inScope ? styles.outOfScope : ''}`}
                  style={{
                    borderLeftColor: getEntropyColor(score.entropy_score),
                    borderLeftWidth: '4px',
                    opacity: inScope ? 1 : 0.5,
                  }}
                  hoverable={inScope}
                  onClick={() => inScope && handleTileClick(score)}
                >
                  <div className={styles.tileContent}>
                    <div className={styles.tileHeader}>
                      <Text strong>{score.artifact_name}</Text>
                      <div
                        className={styles.scoreBadge}
                        style={{ backgroundColor: getEntropyColor(score.entropy_score) }}
                      >
                        {score.entropy_score}/10
                      </div>
                    </div>
                    <div className={styles.tileMetadata}>
                      <Text type="secondary" className={styles.tileState}>
                        {score.state}
                      </Text>
                      {score.correction_factor && (
                        <Text type="secondary" className={styles.tileCorrectionFactor}>
                          CF: {score.correction_factor.toFixed(2)}
                        </Text>
                      )}
                      {!inScope && (
                        <Text type="secondary" style={{ color: '#ff4d4f', fontSize: '12px' }}>
                          Out of scope
                        </Text>
                      )}
                    </div>
                    <Text type="secondary" className={styles.tileDescription}>
                      {interpretation.description}
                    </Text>
                  </div>
                </Card>
              </Tooltip>
            );
          })}
        </div>
      )}

      {sessionType === 'product' && entropyMap?.marketfit_deep_dive_recommended && (
        <Card style={{ marginTop: 24, borderColor: '#1890ff' }}>
          <Typography.Paragraph strong>
            MarketFit alignment warrants a deeper diagnostic.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            Would you like to run a full MarketFit guided session to deepen your understanding of actor, situation, and value alignment?
          </Typography.Paragraph>
          <Space>
            <Link to={`/local-bridge/marketfit/guided?sessionId=${workshopSessionId}`}>
              <Button type="primary">Run MarketFit Guided Session</Button>
            </Link>
            <Button onClick={() => navigateToStep('friction_priorities')}>
              Continue to Friction Priorities
            </Button>
          </Space>
        </Card>
      )}

      {sessionType === 'product' && entropyMap?.bridge_focus === 'development' && (
        <Card style={{ marginTop: 24, borderColor: '#faad14' }}>
          <Typography.Paragraph strong>
            Your highest friction is in Development sequencing.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            Focus on validating the Identify → Define → Design → Implement → Validate sequence. Ensure you are not building before defining, or designing before identifying.
          </Typography.Paragraph>
        </Card>
      )}

      {isLocalBridgeApiEnabled() && !(sessionType === 'product' && entropyMap?.marketfit_deep_dive_recommended) && (
        <div style={{ marginTop: 24 }}>
          <Button type="primary" size="large" onClick={() => navigateToStep('friction_priorities')}>
            Continue to Friction Priorities →
          </Button>
        </div>
      )}

      <Modal
        title="Confirm Entropy Reduction Focus"
        open={confirmModalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Continue → Create Objective"
        cancelText="Cancel"
      >
        {scopeValidationError && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fff2f0', borderRadius: 4 }}>
            <Text type="danger">{scopeValidationError}</Text>
          </div>
        )}
        <p>
          If entropy in <strong>{selectedArea?.artifact_name}</strong> were reduced by ~20%,
          would it materially improve the business right now?
        </p>
        {selectedArea && (
          <div className={styles.confirmDetails}>
            <Space direction="vertical" size="small">
              <Text type="secondary">
                Current entropy score: <strong>{selectedArea.entropy_score}/10</strong>
              </Text>
              {selectedArea.correction_factor && (
                <Text type="secondary">
                  Correction factor: <strong>{selectedArea.correction_factor.toFixed(2)}</strong>
                </Text>
              )}
              <Text type="secondary">
                State: <strong>{selectedArea.state}</strong>
              </Text>
              {selectedArea.ideal_value && selectedArea.actual_value && (
                <Text type="secondary">
                  Ideal: <strong>{selectedArea.ideal_value}</strong> | Actual: <strong>{selectedArea.actual_value}</strong>
                </Text>
              )}
              {scope && (
                <Text type="secondary" style={{ marginTop: 8, fontStyle: 'italic' }}>
                  This subdomain is within the Bridge Session scope.
                </Text>
              )}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};
